import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input } from '@mono/ui-components';
import { Archive, ArchiveRestore, ChevronLeft, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageBody } from '../../components/layout';
import { PageHeader } from '../../components/PageHeader';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useArchiveProject, useUnarchiveProject, useUpdateProject } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
/**
 * Project settings page.
 * Allows editing project name, description, and archive/unarchive.
 */ export function ProjectSettings() {
    const navigate = useNavigate();
    const { projectId, project, isLoading, error, isOwner, isArchived } = useProjectContext();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const updateProject = useUpdateProject();
    const archiveProject = useArchiveProject();
    const unarchiveProject = useUnarchiveProject();
    // Initialize form values when project loads
    useEffect(()=>{
        if (project) {
            setName(project.name);
            setDescription(project.description || '');
        }
    }, [
        project
    ]);
    // Track changes
    useEffect(()=>{
        if (project) {
            const nameChanged = name !== project.name;
            const descChanged = description !== (project.description || '');
            setHasChanges(nameChanged || descChanged);
        }
    }, [
        name,
        description,
        project
    ]);
    const handleSave = async ()=>{
        if (!projectId || !hasChanges) return;
        try {
            await updateProject.mutateAsync({
                projectId,
                name: name.trim(),
                description: description.trim() || undefined
            });
            toast.success('Project updated successfully');
            setHasChanges(false);
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to update project');
            toast.error(message);
        }
    };
    const handleArchive = async ()=>{
        if (!projectId) return;
        try {
            await archiveProject.mutateAsync(projectId);
            toast.success('Project archived successfully');
            navigate('/projects');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to archive project');
            toast.error(message);
        }
    };
    const handleUnarchive = async ()=>{
        if (!projectId) return;
        try {
            await unarchiveProject.mutateAsync(projectId);
            toast.success('Project unarchived successfully');
        } catch (err) {
            const message = apiErrorMessage(err, 'Failed to unarchive project');
            toast.error(message);
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-4 w-24 bg-muted rounded animate-pulse mb-2"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-6 w-32 bg-muted rounded animate-pulse"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "max-w-xl space-y-6",
                        "aria-busy": "true",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-10 bg-muted rounded animate-pulse"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "h-24 bg-muted rounded animate-pulse"
                            })
                        ]
                    })
                })
            ]
        });
    }
    if (error || !project || !projectId) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageHeader, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "pl-8",
                        children: [
                            /*#__PURE__*/ _jsxs(Link, {
                                to: "/projects",
                                className: "text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx(ChevronLeft, {
                                        className: "w-4 h-4"
                                    }),
                                    "Projects"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Project not found"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(PageBody, {
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-red-600 mb-4",
                                children: "This project doesn't exist or you don't have access."
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>navigate('/projects'),
                                children: "Back to Projects"
                            })
                        ]
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PageHeader, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "pl-8",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text-xl font-semibold",
                            children: "Settings"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Project configuration"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(PageBody, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-xl",
                    children: [
                        /*#__PURE__*/ _jsxs("section", {
                            className: "mb-8",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium mb-4",
                                    children: "Project Details"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("label", {
                                                    htmlFor: "project-name",
                                                    className: "block text-sm font-medium mb-1",
                                                    children: "Name"
                                                }),
                                                /*#__PURE__*/ _jsx(Input, {
                                                    id: "project-name",
                                                    value: name,
                                                    onChange: (e)=>setName(e.target.value),
                                                    disabled: !isOwner || isArchived,
                                                    placeholder: "Project name"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            children: [
                                                /*#__PURE__*/ _jsx("label", {
                                                    htmlFor: "project-description",
                                                    className: "block text-sm font-medium mb-1",
                                                    children: "Description"
                                                }),
                                                /*#__PURE__*/ _jsx("textarea", {
                                                    id: "project-description",
                                                    value: description,
                                                    onChange: (e)=>setDescription(e.target.value),
                                                    disabled: !isOwner || isArchived,
                                                    placeholder: "Add a description...",
                                                    rows: 3,
                                                    className: "w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                })
                                            ]
                                        }),
                                        isOwner && !isArchived && /*#__PURE__*/ _jsxs(Button, {
                                            onClick: handleSave,
                                            disabled: !hasChanges || updateProject.isPending,
                                            children: [
                                                /*#__PURE__*/ _jsx(Save, {
                                                    className: "w-4 h-4 mr-1"
                                                }),
                                                updateProject.isPending ? 'Saving...' : 'Save Changes'
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "mb-8",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium mb-4",
                                    children: "Project Info"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex justify-between py-2 border-b",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    children: "Created"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: new Date(project.createdAt).toLocaleDateString('en-US', {
                                                        month: 'long',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex justify-between py-2 border-b",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    children: "Status"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: isArchived ? 'text-yellow-600' : 'text-green-600',
                                                    children: isArchived ? 'Archived' : 'Active'
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex justify-between py-2 border-b",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    children: "Type"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "capitalize",
                                                    children: project.type
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        isOwner && /*#__PURE__*/ _jsxs("section", {
                            className: "pt-4 border-t",
                            children: [
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-lg font-medium mb-4",
                                    children: isArchived ? 'Restore Project' : 'Archive Project'
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground mb-4",
                                    children: isArchived ? 'Restore this project to make it active again. All files and chats will become accessible.' : 'Archiving a project will hide it from the main projects list. Files and chats will be preserved but editing will be disabled.'
                                }),
                                isArchived ? /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    onClick: handleUnarchive,
                                    disabled: unarchiveProject.isPending,
                                    children: [
                                        /*#__PURE__*/ _jsx(ArchiveRestore, {
                                            className: "w-4 h-4 mr-1"
                                        }),
                                        unarchiveProject.isPending ? 'Restoring...' : 'Restore Project'
                                    ]
                                }) : /*#__PURE__*/ _jsxs(Button, {
                                    variant: "outline",
                                    onClick: handleArchive,
                                    disabled: archiveProject.isPending,
                                    className: "text-yellow-600 hover:text-yellow-700 border-yellow-300 hover:border-yellow-400",
                                    children: [
                                        /*#__PURE__*/ _jsx(Archive, {
                                            className: "w-4 h-4 mr-1"
                                        }),
                                        archiveProject.isPending ? 'Archiving...' : 'Archive Project'
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
